import { createGlobalStyle } from 'styled-components';

export const theme = {
  colors: {
    primary: '#1F3A5F',         // Slate navy (professional primary)
    secondary: '#2C4C7A',       // Desaturated navy for hovers/secondary
    accent: '#E67E22',          // Burnt orange (CTA/accent)
    accentSoft: '#F4A261',      // Soft orange for subtle fills
    background: '#FFFFFF',      // Clean white
    sectionAlt: '#FAF7F2',      // Warm off-white for light sections
    lightGray: '#f5f6fa',       // Light gray for borders / alt backgrounds
    text: '#2D3A4A',            // Neutral slate for body text
    textSecondary: '#5A6B7B',   // Muted slate for secondary text
    textLight: '#8A98A5',       // Lighter slate
    white: '#ffffff',
    dark: '#0F172A',            // Deep slate
    navy: '#0a2342',            // Legacy navy kept for compatibility
    gradient: 'linear-gradient(180deg, #FFF7ED 0%, #FFFFFF 100%)', // Subtle warm tint
    // Legacy color mappings for easier migration
    gold: '#E6A64C',            // Softer gold alias
    darkText: '#2D3A4A',
  },
  fonts: {
    primary: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    secondary: "'Playfair Display', 'Georgia', serif",
    brand: "'Great Vibes', 'Playfair Display', serif"
  },
  typography: {
    h1: {
      fontSize: 'clamp(2.5rem, 5vw, 4rem)',
      fontWeight: '700',
      lineHeight: '1.2',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      fontWeight: '700',
      lineHeight: '1.3',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      fontWeight: '600',
      lineHeight: '1.4',
    },
    body: {
      fontSize: '1.125rem',
      lineHeight: '1.7',
      fontWeight: '400',
    },
    small: {
      fontSize: '0.875rem',
      lineHeight: '1.6',
    },
  },
  borderRadius: '12px',
  shadow: {
    sm: '0 4px 6px rgba(0, 0, 0, 0.05)',
    md: '0 10px 25px rgba(0, 0, 0, 0.1)',
    lg: '0 20px 40px rgba(0, 0, 0, 0.15)',
    xl: '0 25px 50px rgba(0, 0, 0, 0.2)',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
    xxxl: '4rem',
  },
  breakpoints: {
    mobile: '320px',
    tablet: '768px',
    desktop: '1024px',
    large: '1440px',
  },
};

export const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&family=Playfair+Display:wght@400;500;600;700;900&family=Great+Vibes&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    font-size: 16px;
  }

  body {
    background: ${theme.colors.background};
    color: ${theme.colors.text};
    font-family: ${theme.fonts.primary};
    font-size: ${theme.typography.body.fontSize};
    font-weight: ${theme.typography.body.fontWeight};
    line-height: ${theme.typography.body.lineHeight};
    letter-spacing: 0.01em;
    margin: 0;
    padding: 0;
    transition: all 0.3s ease;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1 {
    font-family: ${theme.fonts.secondary};
    font-size: ${theme.typography.h1.fontSize};
    font-weight: ${theme.typography.h1.fontWeight};
    line-height: ${theme.typography.h1.lineHeight};
    letter-spacing: ${theme.typography.h1.letterSpacing};
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.text};
  }

  h2 {
    font-family: ${theme.fonts.secondary};
    font-size: ${theme.typography.h2.fontSize};
    font-weight: ${theme.typography.h2.fontWeight};
    line-height: ${theme.typography.h2.lineHeight};
    letter-spacing: ${theme.typography.h2.letterSpacing};
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.text};
  }

  h3 {
    font-family: ${theme.fonts.secondary};
    font-size: ${theme.typography.h3.fontSize};
    font-weight: ${theme.typography.h3.fontWeight};
    line-height: ${theme.typography.h3.lineHeight};
    margin-bottom: ${theme.spacing.md};
    color: ${theme.colors.text};
  }

  h4, h5, h6 {
    font-family: ${theme.fonts.primary};
    font-weight: 600;
    margin-bottom: ${theme.spacing.sm};
    color: ${theme.colors.text};
  }

  p {
    margin-bottom: ${theme.spacing.md};
    color: ${theme.colors.text};
  }

  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    transition: all 0.3s ease;

    &:hover {
      color: ${theme.colors.secondary};
    }
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
    transition: all 0.3s ease;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  .container {
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 ${theme.spacing.lg};

    @media (max-width: ${theme.breakpoints.tablet}) {
      padding: 0 ${theme.spacing.md};
    }
  }

  /* Selection styles */
  ::selection {
    background: ${theme.colors.accent};
    color: ${theme.colors.dark};
  }

  /* Scrollbar styles */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.colors.sectionAlt};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.secondary};
  }
`;

 